import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link, Text } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';

const DogKennelAlternative: React.FC<Props> = ({
  countryConfig,
  citiesAndNeighborhoods,
  language,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_KENNEL_ALTERNATIVE;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x">
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>More affordable than long-term kennel boarding</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Loving care in a sitter's home</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Rather than dropping off your dog at a kennel, they'll stay in the comfort of
                    their sitter's home.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Pet sitters you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every sitter profile has been individually reviewed and approved by Rover.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your dog's perfect match before you book. Request a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Cute photo updates</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Get daily pics of your dog enjoying a round of fetch or snoozing on their
                    sitter's couch.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Affordable, personalized pet care</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Find out{' '}
                    <Link href="/dog-sitting-rates/" target="_blank">
                      how much
                    </Link>{' '}
                    pet sitters near you typically cost.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>
          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Browse to book in 3 steps flat</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Browse profiles of nearby sitters, read reviews, and meet in person if you'd
                    like.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Found your dog's perfect match? Book and pay on Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>

              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Relax</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Your booking is backed by the Rover Guarantee, reservation protection, and 24/7
                    support.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>Why choose Rover instead of boarding kennels?</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Rover is the nation's largest community of pet sitters and dog walkers. Find out why
                pet parents all over the U.S. are choosing Rover over dog kennels.
              </Trans>
            </ServiceTargetAudience.Content>
            <ServiceTargetAudience.List>
              <ServiceTargetAudience.ListItem>
                <Trans>Sitters on Rover have received millions of 5-star reviews</Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  <Text fontWeight="semibold">2+ million</Text> pet parents have booked through
                  Rover
                </Trans>
              </ServiceTargetAudience.ListItem>
              <ServiceTargetAudience.ListItem>
                <Trans>
                  <Text fontWeight="semibold">Millions</Text> of photo updates have been sent
                  through Rover
                </Trans>
              </ServiceTargetAudience.ListItem>
            </ServiceTargetAudience.List>
          </ServiceTargetAudience>

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default DogKennelAlternative;
